import Checkbox from 'components/checkbox';
import InputField from 'components/fields/InputField';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  MdChevronRight,
  MdDownload,
  MdKeyboardArrowDown,
  MdSearch,
} from 'react-icons/md';
import { BallTriangle } from 'react-loader-spinner';
import {
  buildStudentOverviewCsv,
  downloadCsv,
  studentOverviewFileName,
} from 'utils/studentOverviewCsv';

const StudentOverviewTable = ({
  data,
  mcqData,
  qaData,
  mcqAssignmentData,
  qaAssignmentData,
  isLoading,
  placementOverviewData,
  mockData,
  finalMockData,
  highlightStudentId,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const tableRef = useRef(null);

  useEffect(() => {
    if (data?.studentOverviews?.length) {
      setFilteredStudents(data.studentOverviews);
    }
    setSelectedIds(new Set());
  }, [data]);

  useEffect(() => {
    if (!data?.studentOverviews?.length) return;

    if (!searchTerm.trim()) {
      setFilteredStudents(data.studentOverviews);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = data.studentOverviews.filter(
      (student) =>
        student.name?.toLowerCase().includes(searchTermLower) ||
        String(student.contactNumber || '')
          .toLowerCase()
          .includes(searchTermLower) ||
        String(student.email || '').toLowerCase().includes(searchTermLower) ||
        String(student.courseName || '').toLowerCase().includes(searchTermLower)
    );

    setFilteredStudents(filtered);
  }, [searchTerm, data]);

  useEffect(() => {
    if (!highlightStudentId) return;
    if (!filteredStudents?.some((s) => s.studentId === highlightStudentId))
      return;

    setExpandedRows((prev) => ({ ...prev, [highlightStudentId]: true }));

    const rowEl = tableRef.current?.querySelector(
      `[data-student-id="${highlightStudentId}"]`
    );
    rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightStudentId, filteredStudents]);

  const allFilteredSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) => selectedIds.has(s.studentId));

  const toggleSelect = (studentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredStudents.forEach((s) =>
        allFilteredSelected ? next.delete(s.studentId) : next.add(s.studentId)
      );
      return next;
    });
  };

  const toggleRow = (studentId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  const getStatusBadgeColor = (status) => {
    if (status.submitted && !status.isLate)
      return 'bg-green-100 text-green-800';
    if (status.submitted && status.isLate)
      return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusText = (status) => {
    if (status.submitted && !status.isLate) return 'Submitted';
    if (status.submitted && status.isLate) return 'Late';
    return 'Pending';
  };

  const getTestStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'not started':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInterviewStatusBadgeColor = (status) => {
    switch (status) {
      case 'Selected':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'No Rounds':
      case 'On Hold':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  const getOfferStatusBadge = (hasOffer) => {
    return hasOffer
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };

  const findStudentTestData = (studentId, testData) => {
    return testData?.studentOverviews?.find(
      (student) => student.studentId === studentId
    );
  };
  const findPlacementData = (student) => {
    if (!placementOverviewData?.length) return null;

    return placementOverviewData.find(
      (placement) =>
        placement.personalInfo.email.toLowerCase() ===
          student.email?.toLowerCase() ||
        placement.personalInfo.name.toLowerCase() ===
          student.name?.toLowerCase()
    );
  };

  const handleDownloadCSV = () => {
    const students = (data?.studentOverviews || []).filter((s) =>
      selectedIds.has(s.studentId)
    );
    if (students.length === 0) {
      toast.error('Please select at least one student to export.');
      return;
    }

    const csv = buildStudentOverviewCsv({
      students,
      mcqData,
      qaData,
      mcqAssignmentData,
      qaAssignmentData,
      mockData,
      finalMockData,
      findPlacementData,
    });
    downloadCsv(csv, studentOverviewFileName(data?.batchInfo));
    toast.success('CSV downloaded successfully.');
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] w-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <BallTriangle color="#5431ed" />
        </div>
      </div>
    );
  }

  if (!data?.studentOverviews?.length) {
    return (
      <div className="flex h-[400px] w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50">
        <div className="flex flex-col items-center gap-2 p-4 text-center">
          <div className="rounded-full bg-gray-100 p-3">
            <svg
              className="h-6 w-6 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">
            No students found
          </h3>
          <p className="text-sm text-gray-500">
            There are no students enrolled in this class yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="mb-4 flex w-[800px] items-center">
        <div className="relative w-full sm:w-80">
          <InputField
            type="text"
            placeholder="Search by name or contact"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-15 w-full rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-700 dark:bg-navy-800 dark:text-white dark:focus:border-blue-500"
          />
          <MdSearch
            className="absolute right-2 top-3/4 -translate-y-1/2 transform text-gray-400"
            size={20}
          />
        </div>
        {searchTerm && filteredStudents.length === 0 ? (
          <span className="ml-4 text-sm text-gray-500 dark:text-gray-400">
            No students found matching "{searchTerm}"
          </span>
        ) : searchTerm ? (
          <span className="ml-4 text-sm text-gray-500 dark:text-gray-400">
            Found {filteredStudents.length} student(s)
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {selectedIds.size} selected
          </span>
          <button
            onClick={handleDownloadCSV}
            disabled={selectedIds.size === 0}
            className="flex items-center gap-1 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-500 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            <MdDownload size={18} />
            Download CSV ({selectedIds.size})
          </button>
        </div>
      </div>
      <table
        ref={tableRef}
        className="w-full border-collapse bg-white dark:bg-navy-800">
        <thead className="bg-gray-50 dark:border-b dark:border-gray-800 dark:bg-navy-900">
          <tr>
            <th className="w-10 px-4 py-3">
              <Checkbox
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
                title="Select all"
              />
            </th>
            <th className="w-10 px-4 py-3"></th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white ">
              Student Name
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
              Contact
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
              Course
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Practical Assignment
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              MCQ Assignment
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Q&A Assignment
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              MCQ Test
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Q&A Test
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Mock Test
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Final Mock Test
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Attendance
            </th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              {/* Offer Status */}
            </th>
            {/* <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
              Attendance
            </th> */}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {filteredStudents.length === 0 && searchTerm ? (
            <tr>
              <td
                colSpan="14"
                className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No students found matching your search criteria
              </td>
            </tr>
          ) : (
            filteredStudents.map((student) => {
              const mcqStudent = findStudentTestData(
                student.studentId,
                mcqData
              );
              const qaStudent = findStudentTestData(student.studentId, qaData);
              const mcqAssignment = findStudentTestData(
                student.studentId,
                mcqAssignmentData
              );
              const qaAssignment = findStudentTestData(
                student.studentId,
                qaAssignmentData
              );
              const mockStudent = findStudentTestData(student.studentId, mockData);
              const finalMockStudent = findStudentTestData(student.studentId, finalMockData);
              const placementData = findPlacementData(student);

              return (
                <React.Fragment key={student.studentId}>
                  <tr
                    onClick={() => toggleRow(student.studentId)}
                    data-student-id={student.studentId}
                    className={`cursor-pointer hover:bg-gray-50 dark:bg-navy-900 hover:dark:bg-navy-900 ${
                      highlightStudentId === student.studentId
                        ? 'bg-brand-50 dark:bg-navy-700'
                        : ''
                    }`}
                  >
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedIds.has(student.studentId)}
                        onChange={() => toggleSelect(student.studentId)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-gray-500 hover:text-gray-700">
                        {expandedRows[student.studentId] ? (
                          <MdKeyboardArrowDown />
                        ) : (
                          <MdChevronRight />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {student.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-800 dark:text-white/60 ">
                      {student.contactNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-navy-800 dark:text-white/60 ">
                      {student.courseName || '-'}
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {student.statistics.submittedAssignments}/
                          {student.statistics.totalAssignments}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {student.statistics.submissionRate}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {mcqAssignment?.statistics.completedAssignments || 0}/
                          {mcqAssignment?.statistics.totalAssignments || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {mcqAssignment?.statistics.overallPercentage || '0'}%
                        </span>
                      </div>
                    </td>
                    {/* Q&A Assignment */}
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {qaAssignment?.statistics.completedAssignments || 0}/
                          {qaAssignment?.statistics.totalAssignments || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {qaAssignment?.statistics.overallPercentage || '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {mcqStudent?.statistics.completedTests || 0}/
                          {mcqStudent?.statistics.totalTests || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {mcqStudent?.statistics.overallPercentage || '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {qaStudent?.statistics.completedTests || 0}/
                          {qaStudent?.statistics.totalTests || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {qaStudent?.statistics.overallPercentage || '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {mockStudent?.statistics.totalTests || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {mockStudent?.statistics.averageScore || '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {finalMockStudent?.statistics.totalTests || 0}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {finalMockStudent?.statistics.averageScore || '0'}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-navy-800 dark:text-white">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-medium">
                          {student.attendanceDetails
                            ? `${student.attendanceDetails.presentLectures} / ${student.attendanceDetails.totalLectures}`
                            : '- / -'}
                        </span>
                        <span className="mt-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-navy-800">
                          {student.attendanceDetails
                            ? `${student.attendanceDetails.attendancePercentage}%`
                            : '-'}
                        </span>
                      </div>
                    </td>
                    {/* <td className="px-4 py-3 text-navy-800 dark:text-white">
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-medium">
                        {placementData?.interviewMetrics.selected || 0}
                      </span>
                    </div>
                  </td> */}
                    <td className="px-4 py-3 text-center">
                      {placementData && (
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${getOfferStatusBadge(
                            placementData.offerStatus.hasOffer
                          )}`}
                        >
                          {placementData.offerStatus.hasOffer
                            ? 'Has Offer'
                            : 'No Offer'}
                        </span>
                      )}
                      {!placementData && (
                        <span className="text-xs text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                  {expandedRows[student.studentId] && (
                    <tr>
                      <td
                        colSpan={14}
                        className="bg-gray-50 px-4 py-4 dark:bg-navy-900 dark:text-white"
                      >
                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900 ">
                          {/* --- Attendance Details Section --- */}
                          <div className="mb-6">
                            <h4 className="mb-4 text-sm font-semibold">
                              Attendance Details
                            </h4>
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                              {/* Left Panel: Total, Present, Absent */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Lectures:</span>
                                    <span>
                                      {student.attendanceDetails
                                        ?.totalLectures ?? '-'}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Present Lectures:</span>
                                    <span>
                                      {student.attendanceDetails
                                        ?.presentLectures ?? '-'}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Absent Lectures:</span>
                                    <span>
                                      {student.attendanceDetails
                                        ?.absentLectures ?? '-'}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Attendance %:</span>
                                    <span>
                                      {student.attendanceDetails
                                        ?.attendancePercentage ?? '-'}
                                      %
                                    </span>
                                  </p>
                                </div>
                              </div>

                              {/* Right Panel: Absent Dates */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Absent Dates
                                </h5>
                                <div className="max-h-40 space-y-1 overflow-y-auto text-sm">
                                  {student.attendanceDetails?.absentDates
                                    ?.length > 0 ? (
                                    student.attendanceDetails.absentDates.map(
                                      (date, idx) => <p key={idx}>{date}</p>
                                    )
                                  ) : (
                                    <p>None</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-6">
                            <h4 className="mb-4 text-sm font-semibold">
                              Practical Assignment Details
                            </h4>
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                              {/* Statistics Panel */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Assignments:</span>
                                    <span>
                                      {student.statistics.totalAssignments}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Submitted:</span>
                                    <span>
                                      {student.statistics.submittedAssignments}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>On Time:</span>
                                    <span>
                                      {student.statistics.onTimeSubmissions}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Late:</span>
                                    <span>
                                      {student.statistics.lateSubmissions}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Pending:</span>
                                    <span>
                                      {student.statistics.pendingSubmissions}
                                    </span>
                                  </p>
                                </div>
                              </div>

                              {/* Assignment Status Panel */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Assignment Status
                                </h5>
                                <div className="space-y-2">
                                  {student.assignmentStatus.map(
                                    (assignment) => (
                                      <div
                                        key={assignment.assignmentId}
                                        className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0 last:pb-0 dark:border-gray-800"
                                      >
                                        <span
                                          className="max-w-[200px] truncate"
                                          title={assignment.title}
                                        >
                                          {assignment.title}
                                        </span>
                                        <span
                                          className={`rounded-full px-2 py-1 text-xs ${getStatusBadgeColor(
                                            assignment
                                          )}`}
                                        >
                                          {getStatusText(assignment)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/*  Assignmnet Details Section*/}

                          {/* MCQ Assignment Details Section */}
                          <div className="mb-6">
                            <h4 className="mb-4 text-sm font-semibold">
                              MCQ Assignment Details
                            </h4>
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Assignments:</span>
                                    <span>
                                      {mcqAssignment?.statistics
                                        .totalAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Completed:</span>
                                    <span>
                                      {mcqAssignment?.statistics
                                        .completedAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>In Progress:</span>
                                    <span>
                                      {mcqAssignment?.statistics
                                        .inProgressAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {mcqAssignment?.statistics.averageScore ||
                                        0}
                                      %
                                    </span>
                                  </p>
                                </div>
                              </div>
                              {mcqAssignment?.assignmentStatus && (
                                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                  <h5 className="mb-2 text-sm font-medium">
                                    Assignment Status
                                  </h5>
                                  <div className="space-y-2">
                                    {mcqAssignment.assignmentStatus.map(
                                      (assignment) => (
                                        <div
                                          key={assignment.assignmentId}
                                          className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0 last:pb-0 dark:border-gray-800"
                                        >
                                          <span
                                            className="max-w-[200px] truncate"
                                            title={assignment.title}
                                          >
                                            {assignment.title}
                                            <span className="ml-2 ">
                                              ({assignment.assignmentSchedule})
                                            </span>
                                          </span>
                                          <span
                                            className={`rounded-full px-2 py-1 text-xs ${getTestStatusColor(
                                              assignment.status
                                            )}`}
                                          >
                                            {assignment.percentage}%
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Q&A Assignment Details Section */}
                          <div className="mb-6">
                            <h4 className="mb-4 text-sm font-semibold">
                              Q&A Assignment Details
                            </h4>
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Assignments:</span>
                                    <span>
                                      {qaAssignment?.statistics
                                        .totalAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Completed:</span>
                                    <span>
                                      {qaAssignment?.statistics
                                        .completedAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>In Progress:</span>
                                    <span>
                                      {qaAssignment?.statistics
                                        .inProgressAssignments || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {qaAssignment?.statistics.averageScore ||
                                        0}
                                      %
                                    </span>
                                  </p>
                                </div>
                              </div>
                              {qaAssignment?.assignmentStatus && (
                                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                  <h5 className="mb-2 text-sm font-medium">
                                    Assignment Status
                                  </h5>
                                  <div className="space-y-2">
                                    {qaAssignment.assignmentStatus.map(
                                      (assignment) => (
                                        <div
                                          key={assignment.assignmentId}
                                          className="flex items-center justify-between border-b border-gray-100 pb-2  text-sm last:border-0 last:pb-0 dark:border-gray-800"
                                        >
                                          <span
                                            className="max-w-[200px] truncate"
                                            title={assignment.title}
                                          >
                                            {assignment.title}
                                            <span className="ml-2 ">
                                              ({assignment.assignmentSchedule})
                                            </span>
                                          </span>
                                          <span
                                            className={`rounded-full px-2 py-1 text-xs ${getTestStatusColor(
                                              assignment.status
                                            )}`}
                                          >
                                            {assignment.percentage}%
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* /// */}

                          {/* Test Details Section */}
                          <div className="mt-6">
                            <h4 className="mb-4 text-sm font-semibold">
                              Test Details
                            </h4>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              {/* MCQ Tests Panel */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  MCQ Tests Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Tests:</span>
                                    <span>
                                      {mcqStudent?.statistics.totalTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Completed:</span>
                                    <span>
                                      {mcqStudent?.statistics.completedTests ||
                                        0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>In Progress:</span>
                                    <span>
                                      {mcqStudent?.statistics.inProgressTests ||
                                        0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Pending:</span>
                                    <span>
                                      {mcqStudent?.statistics.pendingTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {mcqStudent?.statistics.averageScore || 0}
                                      %
                                    </span>
                                  </p>
                                </div>
                                {mcqStudent?.testStatus && (
                                  <div className="mt-4">
                                    <h6 className="mb-2 text-xs font-medium">
                                      Individual Tests
                                    </h6>
                                    <div className="space-y-2">
                                      {mcqStudent.testStatus.map((test) => (
                                        <div
                                          key={test.testId}
                                          className="flex items-center justify-between border-b border-gray-100 pb-2 text-xs last:border-0 last:pb-0 dark:border-gray-800"
                                        >
                                          <span
                                            className="max-w-[200px] truncate"
                                            title={test.title}
                                          >
                                              {test.title}
                                          </span>
                                          <span
                                            className={`rounded-full px-2 py-1 ${getTestStatusColor(
                                              test.status
                                            )}`}
                                          >
                                            {test.percentage}%
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Q&A Tests Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Tests:</span>
                                    <span>
                                      {qaStudent?.statistics.totalTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Completed:</span>
                                    <span>
                                      {qaStudent?.statistics.completedTests ||
                                        0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>In Progress:</span>
                                    <span>
                                      {qaStudent?.statistics.inProgressTests ||
                                        0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Pending:</span>
                                    <span>
                                      {qaStudent?.statistics.pendingTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {qaStudent?.statistics.averageScore || 0}%
                                    </span>
                                  </p>
                                </div>
                                {qaStudent?.testStatus && (
                                  <div className="mt-4">
                                    <h6 className="mb-2 text-xs font-medium">
                                      Individual Tests
                                    </h6>
                                    <div className="space-y-2">
                                      {qaStudent.testStatus.map((test) => (
                                        <div
                                          key={test.testId}
                                          className="flex items-center justify-between border-b border-gray-100 pb-2 text-xs last:border-0 last:pb-0 dark:border-gray-800"
                                        >
                                          <span
                                            className="max-w-[200px] truncate"
                                            title={test.title}
                                          >
                                            {test.title}
                                          </span>
                                          <span
                                            className={`rounded-full px-2 py-1 ${getTestStatusColor(
                                              test.status
                                            )}`}
                                          >
                                            {test.percentage}%
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {/* Mock Tests Panel */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Mock Tests Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Tests:</span>
                                    <span>
                                      {mockStudent?.statistics.totalTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {mockStudent?.statistics.averageScore || 0}%
                                    </span>
                                  </p>
                                </div>
                                {mockStudent?.testStatus?.length > 0 && (
                                  <div className="mt-4">
                                    <h6 className="mb-2 text-xs font-medium">
                                      Individual Tests
                                    </h6>
                                    <div className="space-y-2">
                                      {mockStudent.testStatus.map((test) => (
                                        <div
                                          key={test.testId}
                                          className="flex items-center justify-between border-b border-gray-100 pb-2 text-xs last:border-0 last:pb-0 dark:border-gray-800"
                                        >
                                          <span
                                            className="max-w-[200px] truncate"
                                            title={test.subjectName}
                                          >
                                            {test.subjectName}
                                            {test.testDate && (
                                              <span className="ml-2 text-gray-500">
                                                ({new Date(test.testDate).toLocaleDateString()})
                                              </span>
                                            )}
                                          </span>
                                          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                                            {test.Marks != null && test.totalMarks
                                              ? `${((test.Marks / test.totalMarks) * 100).toFixed(1)}%`
                                              : 'N/A'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {/* Final Mock Tests Panel */}
                              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                <h5 className="mb-2 text-sm font-medium">
                                  Final Mock Tests Overview
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <p className="flex justify-between">
                                    <span>Total Tests:</span>
                                    <span>
                                      {finalMockStudent?.statistics.totalTests || 0}
                                    </span>
                                  </p>
                                  <p className="flex justify-between">
                                    <span>Average Score:</span>
                                    <span>
                                      {finalMockStudent?.statistics.averageScore || 0}%
                                    </span>
                                  </p>
                                </div>
                                {finalMockStudent?.testStatus?.length > 0 && (
                                  <div className="mt-4">
                                    <h6 className="mb-2 text-xs font-medium">
                                      Individual Tests
                                    </h6>
                                    <div className="space-y-2">
                                      {finalMockStudent.testStatus.map((test) => {
                                        const pct = test.Marks != null && test.totalMarks
                                          ? ((test.Marks / test.totalMarks) * 100).toFixed(1)
                                          : 'N/A';
                                        const pass = test.Marks != null && test.totalMarks != null && (test.Marks / test.totalMarks) >= 0.35;
                                        return (
                                          <div
                                            key={test.testId}
                                            className="flex items-center justify-between border-b border-gray-100 pb-2 text-xs last:border-0 last:pb-0 dark:border-gray-800"
                                          >
                                            <span
                                              className="max-w-[200px] truncate"
                                              title={test.subjectName}
                                            >
                                              {test.subjectName}
                                              {test.testDate && (
                                                <span className="ml-2 text-gray-500">
                                                  ({new Date(test.testDate).toLocaleDateString()})
                                                </span>
                                              )}
                                            </span>
                                            <span className={`rounded-full px-2 py-1 text-xs ${
                                              pass
                                                ? 'bg-green-100 text-green-800'
                                                : test.Marks != null
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}>
                                              {pct}%
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {/* Placement Details Section */}
                              <div>
                                <h4 className="mb-4 text-sm font-semibold">
                                  Placement Details
                                </h4>
                                {placementData ? (
                                  <div className="grid grid-cols-1 gap-4">
                                    {/* Interview Metrics Panel */}
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                      <h5 className="mb-2 text-sm font-medium">
                                        Interview Metrics
                                      </h5>
                                      <div className="space-y-2 text-sm">
                                        <p className="flex justify-between">
                                          <span>Total Interviews:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .totalInterviews
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Attended:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .attended
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Not Attended:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .notAttended
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Selected:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .selected
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Rejected:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .rejected
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>On Hold:</span>
                                          <span>
                                            {
                                              placementData.interviewMetrics
                                                .onHold
                                            }
                                          </span>
                                        </p>
                                      </div>
                                    </div>

                                    {/* Offer Status Panel */}
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-navy-900">
                                      <h5 className="mb-2 text-sm font-medium">
                                        Offer Status
                                      </h5>
                                      <div className="space-y-2 text-sm">
                                        <p className="flex justify-between">
                                          <span>Has Offer:</span>
                                          <span>
                                            {placementData.offerStatus.hasOffer
                                              ? 'Yes'
                                              : 'No'}
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Total Offers:</span>
                                          <span>
                                            {
                                              placementData.offerStatus
                                                .totalOffers
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Accepted Offers:</span>
                                          <span>
                                            {
                                              placementData.offerStatus
                                                .acceptedOffers
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Declined Offers:</span>
                                          <span>
                                            {
                                              placementData.offerStatus
                                                .declinedOffers
                                            }
                                          </span>
                                        </p>
                                        <p className="flex justify-between">
                                          <span>Pending Offers:</span>
                                          <span>
                                            {
                                              placementData.offerStatus
                                                .pendingOffers
                                            }
                                          </span>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center dark:border-gray-700 dark:bg-gray-800">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      No placement data available for this
                                      student
                                    </p>
                                  </div>
                                )}
                              </div>
                              {placementData &&
                                placementData.interviews &&
                                placementData.interviews.length > 0 && (
                                  <div className="mt-6">
                                    <h5 className="mb-2 text-sm font-medium">
                                      Interview History
                                    </h5>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200 rounded-lg border dark:border-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-800">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Company
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Job Title
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Location
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Type
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Status
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                              Date
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                                          {placementData.interviews.map(
                                            (interview, idx) => (
                                              <tr key={idx}>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                  {interview.company}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                  {interview.jobTitle}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                  {interview.location}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                  {interview.jobType}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs">
                                                  <span
                                                    className={`rounded-full px-2 py-1 text-xs ${getInterviewStatusBadgeColor(
                                                      interview.overallStatus
                                                    )}`}
                                                  >
                                                    {interview.overallStatus}
                                                  </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                  {new Date(
                                                    interview.assignedDate
                                                  ).toLocaleDateString()}
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default StudentOverviewTable;
